<template>
  <AppLayout>
    <div class="p-4 md:p-6">

      <!-- ── MOBILE HEADER ── -->
      <div class="md:hidden -mx-4 -mt-4 mb-4">
        <!-- Gradient hero -->
        <div class="bg-gradient-to-br from-indigo-600 via-indigo-500 to-violet-500 px-4 pt-3 pb-5">
          <!-- Back button + Customer Name -->
          <div class="flex items-center gap-3 mb-4">
            <Link :href="'/khata'"
              class="text-white/90 hover:text-white transition active:scale-95">
              <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5"/>
              </svg>
            </Link>
            <div class="flex-1">
              <h1 class="text-xl font-bold text-white">{{ customer.name }}</h1>
              <p class="text-indigo-200 text-xs mt-0.5">{{ customer.shop_name }}</p>
            </div>
          </div>

          <!-- Balance badge -->
          <div v-if="!dataLoading" class="inline-block px-5 py-3 rounded-2xl backdrop-blur-sm"
            :class="currentBalance >= 0
              ? 'bg-red-500/20 border border-red-400/30'
              : 'bg-emerald-500/20 border border-emerald-400/30'">
            <p class="text-xs font-semibold uppercase tracking-wide mb-0.5"
              :class="currentBalance >= 0 ? 'text-red-100' : 'text-emerald-100'">
              {{ currentBalance >= 0 ? 'Maine lene hain' : 'Maine dene hain' }}
            </p>
            <p class="text-2xl font-bold"
              :class="currentBalance >= 0 ? 'text-white' : 'text-white'">
              Rs {{ formatNum(Math.abs(currentBalance)) }}
            </p>
          </div>
          <div v-else class="inline-block px-5 py-3 rounded-2xl bg-white/10 backdrop-blur-sm">
            <div class="h-4 w-24 bg-white/20 rounded animate-pulse mb-1"></div>
            <div class="h-8 w-32 bg-white/20 rounded animate-pulse"></div>
          </div>
        </div>
      </div>

      <!-- ── DESKTOP HEADER ── -->
      <div class="hidden md:flex items-center justify-between mb-6">
        <div class="flex items-center gap-4">
          <Link :href="'/khata'"
            class="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition active:scale-95">
            <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5"/>
            </svg>
          </Link>
          <div>
            <h1 class="text-2xl font-bold text-slate-800 dark:text-white">{{ customer.name }}</h1>
            <p class="text-slate-500 dark:text-slate-400 text-sm mt-0.5">{{ customer.shop_name }}</p>
          </div>
        </div>

        <!-- Balance card on desktop -->
        <div v-if="!dataLoading" class="px-6 py-3 rounded-xl border"
          :class="currentBalance >= 0
            ? 'bg-red-50 dark:bg-red-950/40 border-red-100 dark:border-red-900'
            : 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-100 dark:border-emerald-900'">
          <p class="text-xs font-semibold uppercase tracking-wide"
            :class="currentBalance >= 0 ? 'text-red-500' : 'text-emerald-500'">
            {{ currentBalance >= 0 ? 'Maine lene hain' : 'Maine dene hain' }}
          </p>
          <p class="text-2xl font-bold"
            :class="currentBalance >= 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'">
            Rs {{ formatNum(Math.abs(currentBalance)) }}
          </p>
        </div>
        <div v-else class="px-6 py-3 rounded-xl bg-slate-100 dark:bg-slate-800 animate-pulse">
          <div class="h-4 w-24 bg-slate-200 dark:bg-slate-700 rounded mb-1"></div>
          <div class="h-8 w-32 bg-slate-200 dark:bg-slate-700 rounded"></div>
        </div>
      </div>

      <!-- ── ACTION BUTTONS (MOBILE) ── -->
      <div class="md:hidden flex gap-2 mb-4">
        <button @click="openAdd('sent')"
          class="flex-1 flex items-center justify-center gap-2 bg-red-500 hover:bg-red-400 active:scale-95 text-white
                 text-sm font-semibold px-4 py-3 rounded-xl shadow-sm shadow-red-500/30 transition">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
          </svg>
          MAINE DIYE
        </button>
        <button @click="openAdd('received')"
          class="flex-1 flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 active:scale-95 text-white
                 text-sm font-semibold px-4 py-3 rounded-xl shadow-sm shadow-emerald-500/30 transition">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
          </svg>
          MAINE LIYE
        </button>
      </div>

      <!-- ── ACTION BUTTONS (DESKTOP) ── -->
      <div class="hidden md:flex gap-3 mb-6">
        <button @click="openAdd('sent')"
          class="flex items-center gap-2 bg-red-500 hover:bg-red-400 active:scale-95 text-white
                 text-sm font-semibold px-5 py-2.5 rounded-xl shadow-sm shadow-red-500/30 transition">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
          </svg>
          MAINE DIYE
        </button>
        <button @click="openAdd('received')"
          class="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 active:scale-95 text-white
                 text-sm font-semibold px-5 py-2.5 rounded-xl shadow-sm shadow-emerald-500/30 transition">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
          </svg>
          MAINE LIYE
        </button>
      </div>

      <!-- ── TRANSACTIONS TABLE (DESKTOP) ── -->
      <div class="hidden md:block bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th class="text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-4 py-3">Date</th>
                <th class="text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-4 py-3">Maine Diye</th>
                <th class="text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-4 py-3">Maine Liye</th>
                <th class="text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-4 py-3">Balance</th>
                <th class="text-center text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
              <template v-if="dataLoading">
                <tr v-for="i in 5" :key="i">
                  <td class="px-4 py-3"><div class="h-4 w-20 bg-slate-100 dark:bg-slate-800 rounded animate-pulse"></div></td>
                  <td class="px-4 py-3"><div class="h-4 w-16 bg-slate-100 dark:bg-slate-800 rounded animate-pulse ml-auto"></div></td>
                  <td class="px-4 py-3"><div class="h-4 w-16 bg-slate-100 dark:bg-slate-800 rounded animate-pulse ml-auto"></div></td>
                  <td class="px-4 py-3"><div class="h-4 w-20 bg-slate-100 dark:bg-slate-800 rounded animate-pulse ml-auto"></div></td>
                  <td class="px-4 py-3"><div class="h-4 w-12 bg-slate-100 dark:bg-slate-800 rounded animate-pulse mx-auto"></div></td>
                </tr>
              </template>
              <template v-else>
                <tr v-for="transaction in transactions" :key="transaction.id" class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition">
                  <td class="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                    {{ formatDate(transaction.target_date) }}
                  </td>
                  <td class="px-4 py-3 text-sm font-semibold text-right">
                    <span v-if="transaction.sent > 0" class="text-red-600 dark:text-red-400">
                      Rs {{ formatNum(transaction.sent) }}
                    </span>
                    <span v-else class="text-slate-300 dark:text-slate-700">—</span>
                  </td>
                  <td class="px-4 py-3 text-sm font-semibold text-right">
                    <span v-if="transaction.received > 0" class="text-emerald-600 dark:text-emerald-400">
                      Rs {{ formatNum(transaction.received) }}
                    </span>
                    <span v-else class="text-slate-300 dark:text-slate-700">—</span>
                  </td>
                  <td class="px-4 py-3 text-sm font-semibold text-right"
                    :class="transaction.balance >= 0
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-emerald-600 dark:text-emerald-400'">
                    Rs {{ formatNum(Math.abs(transaction.balance)) }}
                  </td>
                  <td class="px-4 py-3">
                    <div class="flex items-center justify-center gap-2">
                      <button @click="openEdit(transaction)"
                        class="text-indigo-500 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300 transition">
                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"/>
                        </svg>
                      </button>
                      <button @click="openDeleteConfirm(transaction)"
                        class="text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 transition">
                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
                <tr v-if="transactions.length === 0">
                  <td colspan="5" class="px-4 py-12 text-center text-slate-400 dark:text-slate-500 text-sm">
                    No transactions yet
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── TRANSACTIONS LIST (MOBILE) ── -->
      <div class="md:hidden space-y-2">
        <template v-if="dataLoading">
          <div v-for="i in 5" :key="i" class="bg-white dark:bg-slate-900 rounded-xl p-3 border border-slate-200 dark:border-slate-800">
            <div class="flex justify-between items-start mb-2">
              <div class="h-4 w-20 bg-slate-100 dark:bg-slate-800 rounded animate-pulse"></div>
              <div class="h-4 w-16 bg-slate-100 dark:bg-slate-800 rounded animate-pulse"></div>
            </div>
            <div class="h-6 w-24 bg-slate-100 dark:bg-slate-800 rounded animate-pulse"></div>
          </div>
        </template>
        <template v-else>
          <div v-for="transaction in transactions" :key="transaction.id"
            class="bg-white dark:bg-slate-900 rounded-xl p-3 border border-slate-200 dark:border-slate-800">
            <div class="flex justify-between items-start mb-2">
              <p class="text-xs text-slate-500 dark:text-slate-400">
                {{ formatDate(transaction.target_date) }}
              </p>
              <p class="text-xs font-semibold"
                :class="transaction.balance >= 0
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-emerald-600 dark:text-emerald-400'">
                Balance: Rs {{ formatNum(Math.abs(transaction.balance)) }}
              </p>
            </div>
            <div class="flex justify-between items-center">
              <div class="flex gap-3">
                <div v-if="transaction.sent > 0" class="text-red-600 dark:text-red-400 font-semibold text-lg">
                  Rs {{ formatNum(transaction.sent) }}
                </div>
                <div v-if="transaction.received > 0" class="text-emerald-600 dark:text-emerald-400 font-semibold text-lg">
                  Rs {{ formatNum(transaction.received) }}
                </div>
              </div>
              <div class="flex items-center gap-2">
                <button @click="openEdit(transaction)"
                  class="p-2 text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-950 rounded-lg transition">
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"/>
                  </svg>
                </button>
                <button @click="openDeleteConfirm(transaction)"
                  class="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-950 rounded-lg transition">
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div v-if="transactions.length === 0" class="text-center py-12 text-slate-400 dark:text-slate-500 text-sm">
            No transactions yet
          </div>
        </template>
      </div>

    </div>

    <!-- ── ADD / EDIT MODAL ── -->
    <Modal v-if="showModal" @close="closeModal">
      <template #title>{{ editTarget ? 'Edit Khata Entry' : (modalAction === 'sent' ? 'Maine Diye' : 'Maine Liye') }}</template>
      <form @submit.prevent="submitForm" class="space-y-4">

        <!-- Amount -->
        <div class="flex flex-col gap-1">
          <label for="khata-amount" class="text-sm font-medium text-slate-700 dark:text-slate-300">
            Amount (Rs)
          </label>
          <input id="khata-amount" v-model.number="form.amount" type="number" min="1" step="1" required
            class="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2.5
                   text-slate-800 dark:text-white placeholder-slate-400 text-sm
                   focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
            placeholder="0" />
        </div>

        <!-- Source -->
        <div class="flex flex-col gap-1">
          <label for="khata-source" class="text-sm font-medium text-slate-700 dark:text-slate-300">
            Source
          </label>
          <select id="khata-source" v-model="form.source" required
            class="appearance-none bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2.5
                   text-slate-800 dark:text-white text-sm
                   focus:outline-none focus:ring-2 focus:ring-indigo-500 transition cursor-pointer">
            <option value="cash">Cash</option>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="third_party_payment">3rd Party Payment</option>
          </select>
        </div>

        <!-- Bank (if bank_transfer) -->
        <div v-if="form.source === 'bank_transfer'" class="flex flex-col gap-1">
          <label for="khata-bank" class="text-sm font-medium text-slate-700 dark:text-slate-300">
            Bank
          </label>
          <select id="khata-bank" v-model="form.bank_id"
            class="appearance-none bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2.5
                   text-slate-800 dark:text-white text-sm
                   focus:outline-none focus:ring-2 focus:ring-indigo-500 transition cursor-pointer">
            <option value="">Select bank...</option>
            <option v-for="bank in banks" :key="bank.id" :value="bank.id">{{ bank.name }}</option>
          </select>
        </div>

        <!-- Date -->
        <div class="flex flex-col gap-1">
          <label for="khata-date" class="text-sm font-medium text-slate-700 dark:text-slate-300">
            Date
          </label>
          <input id="khata-date" v-model="form.target_date" type="date" required
            class="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2.5
                   text-slate-800 dark:text-white text-sm
                   focus:outline-none focus:ring-2 focus:ring-indigo-500 transition" />
        </div>

        <!-- Notes -->
        <div class="flex flex-col gap-1">
          <label for="khata-notes" class="text-sm font-medium text-slate-700 dark:text-slate-300">
            Notes (optional)
          </label>
          <textarea id="khata-notes" v-model="form.notes" rows="3"
            class="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2.5
                   text-slate-800 dark:text-white placeholder-slate-400 text-sm resize-none
                   focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
            placeholder="Add any notes..."></textarea>
        </div>

        <!-- Proof Upload -->
        <ProofUpload
          v-model="proofFile"
          :existing-proof-url="proofPreviewUrl"
          input-id="khata-proof-input"
          @view-proof="openProof"
        />

        <div class="flex gap-3 pt-2">
          <button type="button" @click="closeModal"
            class="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium
                   text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition">
            Cancel
          </button>
          <button type="submit" :disabled="loading"
            class="flex-1 py-2.5 rounded-xl bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 text-white text-sm font-semibold transition">
            {{ loading ? 'Saving...' : (editTarget ? 'Update' : 'Add Entry') }}
          </button>
        </div>
      </form>
    </Modal>

    <!-- ── PROOF MODAL ── -->
    <Modal v-if="showProofModal" @close="closeProof" size="lg">
      <template #title>Proof</template>
      <div class="flex items-center justify-center p-4">
        <img :src="proofUrl" alt="Proof" class="max-w-full max-h-[70vh] rounded-lg" />
      </div>
    </Modal>

    <!-- Delete confirmation -->
    <ConfirmDialog
      v-if="showDeleteConfirm"
      title="Permanently Delete Khata Entry?"
      message="This action cannot be undone. The khata entry will be permanently removed from your account."
      info-message="WARNING: This will permanently delete this khata entry and remove it from all reports and statistics. This action is irreversible."
      confirm-text="Delete Permanently"
      loading-text="Deleting..."
      variant="danger"
      @confirm="confirmDelete"
      @cancel="closeDeleteConfirm" />

  </AppLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { Link, usePage, router, useForm } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import Modal from '@/components/Modal.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import ProofUpload from '@/components/ProofUpload.vue'
import axios from 'axios'

const props = defineProps({
  customer: Object
})

const transactions = ref([])
const currentBalance = ref(0)
const dataLoading = ref(true)
const showModal = ref(false)
const showProofModal = ref(false)
const proofUrl = ref('')
const loading = ref(false)
const editTarget = ref(null)
const modalAction = ref('sent') // 'sent' or 'received'
const banks = ref([])
const proofFile = ref(null)
const proofPreviewUrl = ref(null)

// Delete state
const showDeleteConfirm = ref(false)
const deleteTarget = ref(null)

const form = ref({
  amount: '',
  source: 'cash',
  bank_id: '',
  target_date: new Date().toISOString().split('T')[0],
  notes: ''
})

async function fetchData() {
  dataLoading.value = true
  try {
    const response = await axios.get(`/khata/${props.customer.id}/details/data`)
    transactions.value = response.data.transactions
    currentBalance.value = response.data.current_balance
  } catch (error) {
    console.error('Error fetching khata details:', error)
  } finally {
    dataLoading.value = false
  }
}

async function fetchBanks() {
  try {
    const response = await axios.get('/banks/data')
    banks.value = response.data.banks
  } catch (error) {
    console.error('Error fetching banks:', error)
  }
}

function openAdd(action) {
  modalAction.value = action
  editTarget.value = null
  form.value = {
    amount: '',
    source: 'cash',
    bank_id: '',
    target_date: new Date().toISOString().split('T')[0],
    notes: ''
  }
  proofFile.value = null
  proofPreviewUrl.value = null
  showModal.value = true
}

function openEdit(transaction) {
  editTarget.value = transaction
  modalAction.value = transaction.sent > 0 ? 'sent' : 'received'
  form.value = {
    amount: transaction.sent > 0 ? transaction.sent : transaction.received,
    source: transaction.source,
    bank_id: transaction.bank_id || '',
    target_date: transaction.target_date,
    notes: transaction.notes || ''
  }
  proofFile.value = null
  proofPreviewUrl.value = transaction.proof_url
  showModal.value = true
}

function closeModal() {
  showModal.value = false
  editTarget.value = null
}

function submitForm() {
  loading.value = true

  const payload = {
    ...form.value,
    action: modalAction.value
  }

  if (proofFile.value) {
    payload.proof = proofFile.value
  }

  if (!editTarget.value) {
    payload.customer_id = props.customer.id
  }

  const req = useForm({ khata: payload })
  const opts = {
    onSuccess: () => {
      closeModal()
      fetchData()
    },
    onError: (errors) => {
      console.error('Error saving khata entry:', errors)
      alert('Error saving entry. Please try again.')
    },
    onFinish: () => {
      loading.value = false
    },
  }

  if (editTarget.value) {
    req.patch(`/khata/${editTarget.value.id}`, opts)
  } else {
    req.post('/khata', opts)
  }
}

function openProof(url) {
  proofUrl.value = url
  showProofModal.value = true
}

function closeProof() {
  showProofModal.value = false
  proofUrl.value = ''
}

// ── Delete khata entry ─────────────────────────────
function openDeleteConfirm(transaction) {
  deleteTarget.value = transaction
  showDeleteConfirm.value = true
}

function closeDeleteConfirm() {
  showDeleteConfirm.value = false
  deleteTarget.value = null
}

function confirmDelete() {
  return new Promise((resolve) => {
    router.delete(`/khata/${deleteTarget.value.id}`, {
      onSuccess: () => {
        closeDeleteConfirm()
        fetchData()
        resolve()
      },
      onError: () => {
        resolve()
      }
    })
  })
}

function formatNum(n) {
  return n.toLocaleString('en-IN')
}

function formatDate(dateStr) {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

onMounted(() => {
  fetchData()
  fetchBanks()
})
</script>
